import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    q: 'How fast can we go live?',
    a: 'Most clients are publishing live AI-assisted content within 7 days of kickoff. Paid campaigns typically launch in 10–14 days after creative approvals.',
  },
  {
    q: 'Does the AI replace our team?',
    a: 'No. SprynAI augments your team. Strategists, designers, and marketers stay in the driver’s seat — the AI handles the heavy lift of production, optimization, and reporting.',
  },
  {
    q: 'Which channels do you support?',
    a: 'Instagram, Facebook, TikTok, LinkedIn, X, YouTube, Pinterest, and Threads for organic. Meta Ads, Google Ads, TikTok Ads, and LinkedIn Ads for paid.',
  },
  {
    q: 'Who owns the content and accounts?',
    a: 'You do — always. We work inside your ad accounts and your channels. If you ever leave, everything stays with you, including data and creative assets.',
  },
  {
    q: 'How is pricing structured?',
    a: 'Flat monthly retainer based on the plan you choose. No long-term contracts on Starter and Growth. Scale plans typically run on a 6-month MSA with quarterly reviews.',
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. SprynAI is SOC2-ready, encrypts data at rest and in transit, and supports SSO on Scale plans. We never train public models on your data.',
  },
];

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      id="faq"
      className="relative bg-background text-foreground py-24 md:py-32 px-8 md:px-28"
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              FAQ
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-medium tracking-[-1.5px] leading-[1.05]">
            Questions, <span className="font-serif italic font-normal">answered.</span>
          </h2>
        </motion.div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 md:px-8 py-5 hover:bg-white/[0.02] transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="text-base md:text-lg font-medium text-foreground">
                    {f.q}
                  </span>
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border text-foreground/80 shrink-0">
                    {isOpen ? (
                      <Minus className="h-3.5 w-3.5" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 md:px-8 pb-6 text-sm md:text-base leading-7 text-muted-foreground">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
